// Self-serve billing, the Plan tab's side (docs/specs/self-serve-billing.md section 5, story P19).
// Types mirror `vd_api_types::billing` (the OpenAPI snapshot docs/api/openapi.json); every route
// is under `/v1/admin/billing`, admins only, and answers 404 with billing off, which the summary
// query turns into `null` ("billing off": the Plan tab keeps today's "Payment: Not set up" row).

import { queryOptions } from "@tanstack/react-query";
import { ALL_PLANS, PLAN_LABEL, type Plan, type SubscriptionStatus } from "@vd/api-client";
import { billingDay } from "../lib/seats";
import { ApiError, api, errorMessage } from "./client";

export type BillingInterval = "month" | "year";
export type ChangeKind = "subscribe" | "seats" | "interval" | "cancel" | "resume";
export type ChangeState = "queued" | "applied" | "needs_action" | "failed";

/** The saved payment method as the console shows it. */
export interface PaymentMethodSummary {
  /** `card` or `us_bank_account`. */
  kind: string;
  /** The card brand or the bank's name. */
  label: string | null;
  last4: string | null;
}

/** `GET /account` (P10). */
export interface BillingAccount {
  provider: string;
  livemode: boolean;
  publishable_key: string;
  customer_ready: boolean;
  payment_method: PaymentMethodSummary | null;
}

export type SetupFlow = "elements" | "checkout";
export type PreferredMethod = "card" | "us_bank_account";

/** `POST /payment-setup`. */
export interface PaymentSetupRequest {
  flow?: SetupFlow;
  prefer?: PreferredMethod;
  /** A fresh id each time the form opens: a retried request returns the same intent. */
  request_id: string;
}

/** What Stripe.js needs to mount the form. */
export interface PaymentSetup {
  flow: SetupFlow;
  client_secret: string;
  publishable_key: string;
  payment_method_order: string[];
}

export interface PlanSeatsView {
  plan: Plan;
  purchased: number;
  assigned: number;
  /** Seats from the next period when a drop is scheduled. */
  next_period: number | null;
  /** Per seat at the company's interval (monthly with none yet), in cents; null: not sold. */
  unit_amount_cents: number | null;
}

export interface BillingChangeView {
  id: string;
  kind: ChangeKind;
  state: ChangeState;
  error_code: string | null;
  /** Stripe's hosted invoice page while the payment needs the customer (3D Secure). */
  action_url: string | null;
  created_at: string;
  updated_at: string;
}

/** `GET /summary`. */
export interface BillingSummary {
  status: SubscriptionStatus;
  internal: boolean;
  items: PlanSeatsView[];
  interval: BillingInterval | null;
  period_start: string | null;
  period_end: string | null;
  renews_at: string | null;
  cancel_at: string | null;
  past_due_since: string | null;
  /** The UTC day (`2026-11-15`) a past-due company is suspended. */
  suspends_on: string | null;
  trial_ends_at: string | null;
  trial_days_left: number | null;
  payment_method: PaymentMethodSummary | null;
  open_change: BillingChangeView | null;
  /** The next invoice's total from Stripe's preview; null without a subscription that renews.
   * Absent from servers older than the contract change: the Plan card sums the seats then. */
  next_invoice_cents?: number | null;
  /** Why the company is suspended or held. */
  suspend_reason?: SuspendReason | null;
  /** The company's open closure (ADR 0062). */
  closure?: BillingClosure | null;
  /** Every plan the company may buy, from the plan book its next change uses. Absent from servers
   * older than the contract change. */
  prices?: PlanPrice[];
}

/** One plan's price per seat, in cents. */
export interface PlanPrice {
  plan: Plan;
  /** Billed monthly. */
  monthly_cents: number;
  /** Per month, billed yearly. */
  annual_monthly_cents: number;
}

/** Why billing or staff stopped the company. */
export type SuspendReason = "payment" | "trial_ended" | "closure" | "hold";

export interface BillingClosure {
  state: string;
  /** When the company's data is deleted. */
  purge_after: string | null;
}

export interface SeatTarget {
  plan: Plan;
  seats: number;
}

/** `POST /changes/preview`: the seats wanted per plan (a plan left out wants none). */
export interface ChangeTarget {
  seats: SeatTarget[];
  interval: BillingInterval;
  /** A payment method just added (`pm_...`), before Kite's mirror has it. */
  payment_method?: string | null;
}

export interface SeatsStopping {
  plan: Plan;
  seats: number;
  at: string | null;
}

export interface ChangePreview {
  kind: ChangeKind;
  due_today_cents: number;
  currency: string;
  next_invoice_at: string | null;
  next_invoice_cents: number | null;
  stopping: SeatsStopping[];
}

/** `POST /changes`. */
export type CreateChange =
  | {
      kind: "plan";
      request_id: string;
      seats: SeatTarget[];
      interval: BillingInterval;
      /** A payment method just added: the server checks it, makes it the default and uses it. */
      payment_method?: string | null;
    }
  | { kind: "cancel"; request_id: string }
  | { kind: "resume"; request_id: string };

/**
 * The publishable key the mock server hands out: the Payment Element renders a stand-in instead
 * of Stripe's form (mock mode and tests; never in a production build).
 */
export const MOCK_PUBLISHABLE_KEY = "pk_test_kite_mock";

/** 409: a change is already queued or waiting on the customer. */
export const CHANGE_IN_PROGRESS = "change_in_progress";
/** 422: the payment method named is not this company's or cannot be used. */
export const PAYMENT_METHOD_INVALID = "payment_method_invalid";
/** 422: the bank account named is still verifying (microdeposits). */
export const PAYMENT_METHOD_VERIFYING = "payment_method_verifying";

const REFUSAL: Record<string, string> = {
  [PAYMENT_METHOD_INVALID]: "That payment method can't be used. Add another.",
  [PAYMENT_METHOD_VERIFYING]: "Your bank account is still being verified.",
};

/** The sentence the sheet shows for a refused preview or change. */
export function refusalText(e: unknown): string {
  if (e instanceof ApiError && REFUSAL[e.code]) return REFUSAL[e.code] as string;
  return errorMessage(e);
}

const BASE = "/v1/admin/billing";

export const billingKeys = {
  all: ["admin", "billing"] as const,
  summary: ["admin", "billing", "summary"] as const,
  account: ["admin", "billing", "account"] as const,
};

export const billingPlan = {
  /** The summary, or `null` with billing off (the server's 404). */
  summary: async (): Promise<BillingSummary | null> => {
    try {
      return await api<BillingSummary>(`${BASE}/summary`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
  account: () => api<BillingAccount>(`${BASE}/account`),
  preview: (body: ChangeTarget, signal?: AbortSignal) =>
    api<ChangePreview>(`${BASE}/changes/preview`, {
      method: "POST",
      body,
      ...(signal ? { signal } : {}),
    }),
  change: (body: CreateChange) => api<BillingChangeView>(`${BASE}/changes`, { method: "POST", body }),
  getChange: (id: string) => api<BillingChangeView>(`${BASE}/changes/${encodeURIComponent(id)}`),
  paymentSetup: (body: PaymentSetupRequest) =>
    api<PaymentSetup>(`${BASE}/payment-setup`, { method: "POST", body }),
};

/** Admins only: the caller checks the role before using it. */
export const billingSummaryQuery = queryOptions({
  queryKey: billingKeys.summary,
  queryFn: billingPlan.summary,
  staleTime: 30_000,
  // While a change applies, the Plan card shows Updating until the job is done.
  refetchInterval: (q) => {
    const s = q.state.data;
    return s?.open_change && s.open_change.state === "queued" ? 2000 : false;
  },
});

// Prices -----------------------------------------------------------------------------------------

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** "$23", or "$23.50": a per-seat price from cents. */
function dollars(cents: number): string {
  return cents % 100 === 0 ? `$${cents / 100}` : USD.format(cents / 100);
}

/**
 * One plan's price per seat in cents, monthly or per month billed yearly: the server's price book
 * (`prices`, from the plan book the next change uses). A server older than that sends none; then
 * only a plan the company holds has a price, and only at the company's own interval.
 */
export function seatPriceCents(s: BillingSummary, plan: Plan, interval: BillingInterval): number | null {
  if (s.prices) {
    const p = s.prices.find((x) => x.plan === plan);
    return p ? (interval === "month" ? p.monthly_cents : p.annual_monthly_cents) : null;
  }
  const held = s.items.find((i) => i.plan === plan)?.unit_amount_cents ?? null;
  if (held === null || interval !== (s.interval ?? "month")) return null;
  return interval === "year" ? Math.round(held / 12) : held;
}

/** "$239 a seat a month", "$199 a seat a month, billed yearly", or null with no price. */
export function priceLine(s: BillingSummary, plan: Plan, interval: BillingInterval): string | null {
  const cents = seatPriceCents(s, plan, interval);
  if (cents === null) return null;
  return interval === "month"
    ? `${dollars(cents)} a seat a month`
    : `${dollars(cents)} a seat a month, billed yearly`;
}

/** The plans the sheet offers: those with a price, and any the company holds; every plan from an
 * older server. */
export function sheetPlans(s: BillingSummary): Plan[] {
  if (!s.prices) return ALL_PLANS;
  return ALL_PLANS.filter((p) => s.prices?.some((x) => x.plan === p) || s.items.some((i) => i.plan === p));
}

// Formatting --------------------------------------------------------------------------------------

/** "$1,135.00" from cents. */
export function fmtCents(cents: number): string {
  return USD.format(cents / 100);
}

const LONG = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

/** "Nov 1, 2027". */
export function billingDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : LONG.format(d);
}

/** "Nov 15" from a UTC day (`2026-11-15`) or a timestamp. */
export function dayOf(isoOrDay: string): string {
  return billingDay(/^\d{4}-\d{2}-\d{2}$/.test(isoOrDay) ? `${isoOrDay}T00:00:00Z` : isoOrDay);
}

// State -------------------------------------------------------------------------------------------

/**
 * The trial ended without a plan: the server's suspend reason. A server older than the contract
 * change sends none; then a suspended company with no subscription (so no interval) is one.
 */
export function trialEnded(s: BillingSummary): boolean {
  if (s.status !== "suspended") return false;
  if (s.suspend_reason != null) return s.suspend_reason === "trial_ended";
  return s.interval === null;
}

/** The company has no plan to change: the sheet opens as "Choose plan". */
export function choosesPlan(s: BillingSummary): boolean {
  return !s.internal && (s.status === "trialing" || s.status === "canceled" || trialEnded(s));
}

/** A cancel is set for the period end. */
export function canceling(s: BillingSummary): boolean {
  return s.cancel_at !== null && s.status !== "canceled";
}

/** A change is being applied (or waits on the customer's bank). */
export function updating(s: BillingSummary): boolean {
  const c = s.open_change;
  return !!c && (c.state === "queued" || c.state === "needs_action");
}

/** The header's status line (DESIGN.md "Billing (console)", Header). */
export function statusLine(s: BillingSummary): string {
  if (s.internal || s.status === "internal") return "Internal";
  switch (s.status) {
    case "trialing": {
      const n = s.trial_days_left;
      return n === null ? "Trial" : `Trial. ${n} ${n === 1 ? "day" : "days"} left`;
    }
    case "past_due":
      return s.past_due_since ? `Past due since ${dayOf(s.past_due_since)}` : "Past due";
    case "suspended":
      return "Paused";
    case "canceled":
      return "Canceled";
    default:
      if (canceling(s) && s.cancel_at) return `Active. Ends ${dayOf(s.cancel_at)}`;
      return s.renews_at ? `Active. Renews ${dayOf(s.renews_at)}` : "Active";
  }
}

/**
 * The next invoice's total: Stripe's preview from the server, or (from an older server) the
 * seats billed at renewal, each plan at its next period's count when a drop is scheduled.
 */
export function renewalCents(s: BillingSummary): number {
  if (s.next_invoice_cents != null) return s.next_invoice_cents;
  return s.items.reduce((sum, i) => sum + (i.unit_amount_cents ?? 0) * (i.next_period ?? i.purchased), 0);
}

/** "Sales Pro, 3 seats": the trial's plan. */
export function trialLine(s: BillingSummary): string {
  const held = s.items.filter((i) => i.purchased > 0);
  const main = held[held.length - 1] ?? s.items[0];
  if (!main) return "";
  return `${PLAN_LABEL[main.plan]}, ${main.purchased} ${main.purchased === 1 ? "seat" : "seats"}`;
}

/**
 * When a trial that ended is deleted: the closure's `purge_after` (ADR 0062), or, while the
 * server has none, the trial's end plus the closure's 30 days.
 */
export function purgeDate(s: BillingSummary): string {
  if (s.closure?.purge_after) return s.closure.purge_after;
  const end = new Date(s.trial_ends_at ?? s.period_end ?? Date.now());
  return new Date(end.getTime() + 30 * 86_400_000).toISOString();
}

export type StripKind = "past_due" | "suspended" | "trial_ended" | "confirm";

/** The one strip an admin sees over every Admin page, or null. */
export function stripOf(s: BillingSummary | null | undefined): StripKind | null {
  if (!s || s.internal) return null;
  if (s.status === "past_due") return "past_due";
  if (s.status === "suspended") {
    // A closure or a staff hold has its own strip (the closure's wins over every Billing strip).
    if (s.suspend_reason === "closure" || s.suspend_reason === "hold") return null;
    return trialEnded(s) ? "trial_ended" : "suspended";
  }
  if (s.open_change?.state === "needs_action" && s.open_change.action_url) return "confirm";
  return null;
}

/** A fresh id for a form opening or an action: a retried request returns the same result. */
export function requestId(): string {
  return crypto.randomUUID();
}
